import { useCallback, useEffect, useMemo, useState } from "react"

import _ from "lodash"

import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useUrlParams } from "@l2r-front/l2r-utils"

import { documentsFiltersConfig } from "../../constants/documentsFiltersConfig"
import { ATTACHMENT_TYPE_FILTER } from "../../constants/filters"
import { LINEAR_ATTACHMENTS } from "../../constants/modules"
import { useDocumentAuthors } from "../../hooks/queries/useDocumentAuthors"

import { DocumentDispatchContext, DocumentStateContext, initialState } from "./DocumentContext.context"
import { getFiltersFromSearchParams, getSearchParamsFromFilters, setFiltersSearchParams } from "./DocumentContext.helpers"

export function DocumentContextProvider(props) {
    const { children } = props
    const [documentState, setDocumentState] = useState(initialState)
    const { data: documentAuthors } = useDocumentAuthors()

    const {
        deleteParam,
        getParam,
        getParams,
        setParam,
    } = useUrlParams()

    const setHoveredAttachedObject = useCallback((attachedObject) => {
        setDocumentState(prevState => ({
            ...prevState,
            hoveredAttachedObject: attachedObject,
        }))
    }, [])

    const setFilters = useCallback((newFilters) => {
        setDocumentState(value => ({
            ...value,
            filters: newFilters,
        }))

        setFiltersSearchParams(newFilters, setParam, deleteParam)
    }, [deleteParam, setParam])

    const applyFilters = useCallback((filters) => {
        if (!_.isEqual(filters, documentState.filters)) {
            setFilters(filters)
        }
    }, [setFilters,
        documentState.filters])

    const apiFilters = useMemo(() => {
        const filters = documentState.filters || {}

        return Object.entries(filters).reduce((acc, [filterKey, filterValue]) => {
            if (filterValue?.length === 0 || filterValue === null || filterValue === undefined) {
                return acc
            }

            const config = documentsFiltersConfig[filterKey]
            const queryParam = config?.queryParam
            if (!queryParam) {
                return acc
            }

            acc[queryParam] =
                filterKey === ATTACHMENT_TYPE_FILTER
                    ? filterValue.flatMap(t =>
                        LINEAR_ATTACHMENTS.includes(t) ? t.toUpperCase() : t,
                    )
                    : config.getApiFilterValue(filterValue)

            return acc
        }, {})
    }, [documentState.filters])

    useEffect(() => {
        const filtersFromParams = getFiltersFromSearchParams(getParams)

        if (!_.isEqual({ ...documentState.filters, ...filtersFromParams }, documentState.filters)) {
            setFilters({
                ...documentState.filters,
                ...filtersFromParams,
            })
        } else if (documentState.filters && Object.keys(documentState.filters).length) {
            const paramsFilters = getSearchParamsFromFilters(documentState.filters)
            for (const [key, value] of Object.entries(paramsFilters)) {
                const existingParam = getParam(key) === value
                if (!existingParam) {
                    setParam(key, value)
                }
            }
        }
    }, [
        documentState.filters,
        getParam,
        getParams,
        setParam,
        setFilters])

    useEffect(() => {
        if (documentState.filters?.author?.length && documentAuthors) {
            const newAuthors = documentState.filters.author.filter(author => {
                return documentAuthors.find(u => u.uuid.toString() === author.toString())
            })
            if (newAuthors.length !== documentState.filters.author.length) {
                const filters = {
                    ...documentState.filters,
                    author: newAuthors,
                }
                setFilters(filters)
                applyFilters(filters)
            }
        }
    }, [applyFilters, documentAuthors, documentState.filters, setFilters])

    const stateValue = useMemo(() => {
        return {
            apiFilters,
            filters: documentState.filters,
            hoveredAttachedObject: documentState.hoveredAttachedObject,
        }
    }, [apiFilters,
        documentState.filters,
        documentState.hoveredAttachedObject])

    const dispatchValue = useMemo(() => {
        return {
            applyFilters,
            setFilters,
            setHoveredAttachedObject,
        }
    }, [applyFilters,
        setFilters,
        setHoveredAttachedObject])

    return (
        <DocumentStateContext.Provider value={stateValue}>
            <DocumentDispatchContext.Provider value={dispatchValue}>
                {children}
            </DocumentDispatchContext.Provider>
        </DocumentStateContext.Provider>
    )
}

DocumentContextProvider.propTypes = {
    children: PropTypes.node,
}
